import { ParamMap, Params } from '@angular/router';
import { SpsPruefstatus, SpsSignalRichtung } from './sps-signal.model';

/*
 * Filter der Originalzeilen EINER importierten SPS-Liste
 * (GET api/sps/listen/{id}/eintraege) – Spiegelbild von
 * `SpsListenEintraegeFilter` im Backend – samt Abbildung auf URL-Query-
 * Parameter, damit der Filterzustand den Wechsel in die Signaldetails und
 * zurück überlebt.
 */

/** Sortierfelder des Backends; Suffix `_desc` für absteigend. */
export type SpsListenSortierung =
  | 'zeile' | 'zeile_desc'
  | 'adresse' | 'adresse_desc'
  | 'name' | 'name_desc'
  | 'status' | 'status_desc'
  | 'station' | 'station_desc';

export const SPS_LISTEN_SORTIERUNGEN: { wert: SpsListenSortierung; text: string }[] = [
  { wert: 'zeile', text: 'Excel-Zeile aufsteigend' },
  { wert: 'zeile_desc', text: 'Excel-Zeile absteigend' },
  { wert: 'adresse', text: 'Adresse aufsteigend' },
  { wert: 'adresse_desc', text: 'Adresse absteigend' },
  { wert: 'name', text: 'Signalname A–Z' },
  { wert: 'name_desc', text: 'Signalname Z–A' },
  { wert: 'status', text: 'Prüfstatus' },
  { wert: 'station', text: 'Station' }
];

export interface SpsListenFilter {
  /** 1-basiert. */
  seite: number;
  /** Höchstens 500. */
  groesse: number;
  /** Volltext über Signalname, Adresse, BMK, Beschreibung, Station, Anschluss, Kanal, Ort, Platzierung, Datentyp. */
  suche: string;
  /** Richtung des verknüpften Signals; null = alle. */
  richtung: SpsSignalRichtung | null;
  /** Prüfstatus des verknüpften gemeinsamen Signals; null = alle. */
  status: SpsPruefstatus | null;
  /** Wie in der SPS-Prüfung: Ungeprüft oder Nicht OK. */
  nurOffen: boolean;
  sortierung: SpsListenSortierung;

  /**
   * Nur Zeilen dieses Anlagenteils (Id aus der Anlagenteilverwaltung des Projekts) –
   * eigener Anlagenteil der Zeile oder der eindeutige Anlagenteil der bestätigten
   * Quellen ihres Signals; null = alle.
   */
  anlagenteilId: number | null;
  /** Nur Zeilen ohne (eindeutigen) Anlagenteil. Hat Vorrang vor `anlagenteilId`. */
  ohneAnlagenteil: boolean;

  name: string;
  /** Originaladresse der Zeile oder normalisierte Adresse des Signals. */
  adresse: string;
  bmk: string;
  beschreibung: string;
  station: string;
  anschluss: string;
  kanal: string;
  ort: string;
  platzierung: string;
  datenTyp: string;
}

export const SPS_LISTEN_STANDARD_GROESSE = 100;

/** Textfelder des Filters, die 1:1 als Query-Parameter übertragen werden. */
export const SPS_LISTEN_TEXTFELDER = [
  'suche', 'name', 'adresse', 'bmk', 'beschreibung', 'station', 'anschluss', 'kanal', 'ort', 'platzierung', 'datenTyp'
] as const;

/** Wert der Anlagenteil-Auswahl für „Ohne Anlagenteil“ (Select-Option und URL-Parameter). */
export const SPS_ANLAGENTEIL_OHNE = 'ohne';

const RICHTUNGEN: SpsSignalRichtung[] = ['Eingang', 'Ausgang', 'Unbekannt'];
const STATUS: SpsPruefstatus[] = ['Ungeprueft', 'Ok', 'NichtOk', 'Reserve'];

export function leererSpsListenFilter(): SpsListenFilter {
  return {
    seite: 1,
    groesse: SPS_LISTEN_STANDARD_GROESSE,
    suche: '',
    richtung: null,
    status: null,
    nurOffen: false,
    sortierung: 'zeile',
    anlagenteilId: null,
    ohneAnlagenteil: false,
    name: '',
    adresse: '',
    bmk: '',
    beschreibung: '',
    station: '',
    anschluss: '',
    kanal: '',
    ort: '',
    platzierung: '',
    datenTyp: ''
  };
}

export function istSpsListenSortierung(wert: string | null | undefined): wert is SpsListenSortierung {
  return SPS_LISTEN_SORTIERUNGEN.some(s => s.wert === wert)
    || (!!wert && wert.endsWith('_desc') && SPS_LISTEN_SORTIERUNGEN.some(s => s.wert === wert.slice(0, -5)));
}

/** True, wenn irgendeine Bedingung oder eine andere Sortierung als die Excel-Reihenfolge gesetzt ist. */
export function spsListenFilterAktiv(f: SpsListenFilter): boolean {
  return !!(f.richtung || f.status || f.nurOffen || f.sortierung !== 'zeile'
    || f.anlagenteilId != null || f.ohneAnlagenteil
    || SPS_LISTEN_TEXTFELDER.some(k => f[k].trim() !== ''));
}

/**
 * Wert der Anlagenteil-Auswahl (mat-select): `null` = alle, `'ohne'` = ohne
 * Anlagenteil, sonst die Id. Gemeinsam für Listen und Prüfung.
 */
export function anlagenteilAuswahlWert(anlagenteilId: number | null | undefined, ohne: boolean | null | undefined): number | string | null {
  if (ohne) { return SPS_ANLAGENTEIL_OHNE; }
  return anlagenteilId ?? null;
}

/** Umkehrung von `anlagenteilAuswahlWert` – auch für den URL-Parameter `anlagenteil`. */
export function anlagenteilAuswahlLesen(wert: number | string | null | undefined): { anlagenteilId: number | null; ohneAnlagenteil: boolean } {
  if (wert === SPS_ANLAGENTEIL_OHNE) {
    return { anlagenteilId: null, ohneAnlagenteil: true };
  }

  const id = Number(wert);
  return Number.isFinite(id) && id > 0
    ? { anlagenteilId: Math.floor(id), ohneAnlagenteil: false }
    : { anlagenteilId: null, ohneAnlagenteil: false };
}

/** Filter aus Query-Parametern; fehlende oder ungültige Werte bekommen den Standard. */
export function spsListenFilterAusParams(params: ParamMap): SpsListenFilter {
  const f = leererSpsListenFilter();

  const richtung = params.get('richtung');
  if (RICHTUNGEN.includes(richtung as SpsSignalRichtung)) {
    f.richtung = richtung as SpsSignalRichtung;
  }

  const status = params.get('status');
  if (STATUS.includes(status as SpsPruefstatus)) {
    f.status = status as SpsPruefstatus;
  }

  const sortierung = params.get('sortierung');
  if (istSpsListenSortierung(sortierung)) {
    f.sortierung = sortierung;
  }

  f.nurOffen = params.get('nurOffen') === 'true';

  const anlagenteil = anlagenteilAuswahlLesen(params.get('anlagenteil'));
  f.anlagenteilId = anlagenteil.anlagenteilId;
  f.ohneAnlagenteil = anlagenteil.ohneAnlagenteil;

  for (const k of SPS_LISTEN_TEXTFELDER) {
    f[k] = params.get(k) ?? '';
  }

  const seite = Number(params.get('seite'));
  if (Number.isFinite(seite) && seite >= 1) {
    f.seite = Math.floor(seite);
  }

  const groesse = Number(params.get('groesse'));
  if (Number.isFinite(groesse) && groesse >= 1 && groesse <= 500) {
    f.groesse = Math.floor(groesse);
  }

  return f;
}

/** Query-Parameter eines Filters – nur Werte abseits des Standards, damit die URL lesbar bleibt. */
export function spsListenFilterAlsParams(f: SpsListenFilter): Params {
  const p: Params = {};

  if (f.richtung) { p['richtung'] = f.richtung; }
  if (f.status) { p['status'] = f.status; }
  if (f.nurOffen) { p['nurOffen'] = 'true'; }
  if (f.sortierung !== 'zeile') { p['sortierung'] = f.sortierung; }

  const anlagenteil = anlagenteilAuswahlWert(f.anlagenteilId, f.ohneAnlagenteil);
  if (anlagenteil != null) { p['anlagenteil'] = anlagenteil; }

  for (const k of SPS_LISTEN_TEXTFELDER) {
    const wert = f[k].trim();
    if (wert) { p[k] = wert; }
  }

  if (f.seite > 1) { p['seite'] = f.seite; }
  if (f.groesse !== SPS_LISTEN_STANDARD_GROESSE) { p['groesse'] = f.groesse; }

  return p;
}
